import { useCallback } from 'react';

import { useAccount, useSignMessage } from 'wagmi';

import { useAcceptDisclaimer } from '@services/api/bonds/bonds.api';

import {
  isHwOverflowError,
  mapWagmiError,
} from '@helpers/common/error.helpers';

import showToast from '@components/common/Toast';

import { BOND_DISCLAIMER_TEXTS } from '@constants/bonds/disclaimer.constants';
import { ToastType } from '@constants/common/toast.constants';

import { BondDisclaimerVersion } from '@dto/bonds/bonds.dto';

// Mirror of backend reconstruction — both sides build the same message bytes
// for the same `version + timestamp`, so ecrecover matches.
const buildSignMessage = (
  version: BondDisclaimerVersion,
  timestamp: number,
): string => `${BOND_DISCLAIMER_TEXTS[version]}\n\nTimestamp: ${timestamp}`;

export const useAcceptBondsDisclaimer = () => {
  const { address } = useAccount();
  const { signMessageAsync, isPending: isSigning } = useSignMessage();
  const { mutate: acceptDisclaimer, isPending: isPosting } =
    useAcceptDisclaimer();

  const accept = useCallback(async () => {
    if (!address) return;
    const userAddress = address;
    const timestamp = Date.now();

    // Primary path: long format — software wallets see the actual disclaimer.
    try {
      const signature = await signMessageAsync({
        account: userAddress,
        message: buildSignMessage(BondDisclaimerVersion.V1, timestamp),
      });
      acceptDisclaimer({
        address: userAddress,
        disclaimerVersion: BondDisclaimerVersion.V1,
        signature,
        timestamp,
      });
      return;
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      if (!isHwOverflowError(error)) {
        console.log('useAcceptBondsDisclaimer (long):', error);
        showToast({ title: mapWagmiError(error) }, ToastType.Error);
        return;
      }
      showToast(
        {
          title:
            'Your wallet has a message size limit. Please sign the short version.',
        },
        ToastType.Info,
      );
    }

    // Fallback: short hash-based attestation for HW wallets.
    try {
      const signature = await signMessageAsync({
        account: userAddress,
        message: buildSignMessage(BondDisclaimerVersion.V1Hash, timestamp),
      });
      acceptDisclaimer({
        address: userAddress,
        disclaimerVersion: BondDisclaimerVersion.V1Hash,
        signature,
        timestamp,
      });
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      console.log('useAcceptBondsDisclaimer (hash):', error);
      showToast({ title: mapWagmiError(error) }, ToastType.Error);
    }
  }, [address, signMessageAsync, acceptDisclaimer]);

  return {
    accept,
    isPending: isSigning || isPosting,
  };
};
